import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, Fade, IconButton, Button } from '@material-ui/core';
import { getPaymentMethods, postBookingIssuePayment } from 'api/helpers/api-constants';
import fetcher from 'api/helpers/fetcher';
import { postParamRequest } from 'api/helpers/postFetcher';
import PaymentResponseRender from './PaymentResponse';
import currencyFormat from 'utils/currencyFormat';
import CloseIcon from '@material-ui/icons/Close';
import CreditCardIcon from '@material-ui/icons/CreditCard';

interface PaymentMethod {
  type: string;
  method: string;
  feeRate: number;
}

interface PaymentThreedProps {
  bookingKey: string;
  totalPrice: number
}

const PaymentThreed: React.FC<PaymentThreedProps> = (
  bookingKey,
  totalPrice,
  ) => {
    const { t } = useTranslation();
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [confirmationDialog, setConfirmationDialog] = useState(false);
    const [pageThreeD, setpageThreeD] = useState<any>(null);
    const [selectedPaymentType, setSelectedPaymentType] = useState<string>('');
    const [loading, setLoading] = useState(false);
  
    const handlePaymentClick = async () => {
      setLoading(true);
      const response = await fetcher(getPaymentMethods());
      if (response) {
        setPaymentMethods(response);
        setOpenDialog(true);
      }
      setLoading(false);
    };
  
    const handleCloseDialog = () => {
      setOpenDialog(false);
    };
  
    const handleSelectPayment = async (selectedPaymentType: string, selectedPaymentMethod?: string) => {
      setSelectedPaymentType(selectedPaymentType);
      setOpenDialog(false);
      setConfirmationDialog(true);
    };
  
    const handleConfirmPayment = async () => {
      setLoading(true);
      setConfirmationDialog(false);
  
      const params = {
        bookingKey: bookingKey.bookingKey,
        paymentType: selectedPaymentType,
        redirectUrl: selectedPaymentType ? window.location.href : '',
      };
  
      const response = await postParamRequest(postBookingIssuePayment(), params);
      if (response) {
        setpageThreeD(response);
      }
      setLoading(false);
    };
  
    const handleCancelPayment = () => {
      setConfirmationDialog(false);
    };
  
  return (
    <div>
      <Button 
        onClick={handlePaymentClick}
        disabled={loading}
        variant='contained'
        color="primary"
        startIcon={<CreditCardIcon fontSize="small" />}
        className="bg-primary-6000 text-white hover:bg-primary-700 px-6 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
        ) : null}
        {t('booking.purchase')}
      </Button>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          style: { 
            borderRadius: '16px', 
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle disableTypography className="flex items-center justify-between pt-6 px-6 pb-2">
          <h2 className="text-xl font-bold text-gray-900">
            {t("flightbooking.ticketing.please-select-your-preferred-payment-method")}
          </h2>
          <IconButton 
            aria-label="close" 
            onClick={handleCloseDialog} 
            size="small" 
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors duration-200"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className="px-6 py-4">
          <div className="grid grid-cols-1 gap-4 mt-2">
            {paymentMethods.length > 0 &&
              paymentMethods.map((method: PaymentMethod, index: number) => (
                <Button
                  key={index}
                  onClick={() => handleSelectPayment(method.type, method.method)}
                  className="group w-full text-left focus:outline-none transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:shadow-lg"
                >
                  <div className="flex items-center justify-between p-5 border border-gray-200 rounded-xl bg-white hover:border-primary-500 transition-all duration-200">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center shadow-sm group-hover:bg-primary-200 transition-colors duration-200">
                        <CreditCardIcon fontSize="small" />
                      </div>
                      <div className="text-gray-800 text-lg font-medium">
                        {t("b2c-app.payment.payment-type.credit-card")}
                      </div>
                    </div>
                    <div className="text-primary-600 p-3 text-xl font-bold tracking-tight">
                      {currencyFormat(bookingKey.totalPrice * method.feeRate)}
                    </div>
                  </div>
                </Button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog 
        open={confirmationDialog} 
        onClose={handleCancelPayment}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          style: { 
            borderRadius: '16px', 
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle disableTypography className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900">
            {t("b2c.booking.redirectpaymentpage")}
          </h2>
          <IconButton 
            aria-label="close" 
            onClick={handleCancelPayment} 
            size="small" 
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors duration-200"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-6">
          <p className="mb-8 text-center text-gray-700 text-lg">{t("generals.are-you-sure-you-want-to-proceed")}</p>
          <div className="flex justify-end space-x-4">
            <Button 
              variant='outlined'
              onClick={handleCancelPayment}
              className="px-6 py-2.5 rounded-lg font-medium border-2 hover:bg-gray-50 transition-all duration-200"
            >
              {t("b2c-app.generals.cancel")}
            </Button>
            <Button 
              color='primary'
              variant='contained'
              onClick={handleConfirmPayment}
              disabled={loading}
              className="px-6 py-2.5 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : null}
              {t("b2c-app.confirmation-request.confirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {pageThreeD && <PaymentResponseRender pageThreeD={pageThreeD} />}
    </div>
  );
};

export default PaymentThreed;
